import React, { useState } from 'react';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { create as createDept, update as updateDept, remove as removeDept } from '../../api/departments';

const C = {
  bg: '#0a0e17', surface: '#131a2b', elevated: '#1a2235',
  border: 'rgba(255,255,255,0.08)', text: '#e2e8f0', muted: '#64748b',
  subtle: '#94a3b8', green: '#22c55e', red: '#ef4444', blue: '#3b82f6',
  amber: '#f59e0b',
};

// ─── Form Modal ───────────────────────────────────────────────────────────────

export function DepartmentFormModal({ data, companies, sites, onSave, onClose }) {
  const [form, setForm] = useState({
    companyId: data?.companyId || '',
    siteId:    data?.siteId    || '',
    name:      data?.name      || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState('');
  const isEdit = !!data?.id;

  // Sites filtered to selected company (only relevant when creating)
  const filteredSites = sites.filter(s => s.companyId === form.companyId);

  function setCompany(companyId) {
    setForm(f => ({ ...f, companyId, siteId: '' }));
  }

  async function handleSave() {
    if (!form.companyId)    return setErr('Company is required');
    if (!form.siteId)       return setErr('Site is required');
    if (!form.name.trim())  return setErr('Department name is required');
    setSaving(true); setErr('');
    try {
      if (isEdit) {
        const updated = await updateDept(data.id, { name: form.name.trim() });
        onSave(updated);
      } else {
        const created = await createDept({ companyId: form.companyId, siteId: form.siteId, name: form.name.trim() });
        onSave(created);
      }
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to save');
      setSaving(false);
    }
  }

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' };
  const modal   = { background: C.surface, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '24px', width: '400px', maxWidth: '95vw' };
  const label   = { fontSize: '11px', fontWeight: 600, color: C.muted, marginBottom: '5px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.4px' };
  const field   = { marginBottom: '16px' };
  const inputSt = { width: '100%', padding: '9px 12px', borderRadius: '8px', border: `1px solid ${C.border}`, background: C.elevated, color: C.text, fontSize: '13px', fontFamily: 'inherit', boxSizing: 'border-box' };
  const readSt  = { ...inputSt, background: C.bg, color: C.subtle, cursor: 'not-allowed' };

  const companyName = companies.find(c => c.id === form.companyId)?.name || '';
  const siteName    = sites.find(s => s.id === form.siteId)?.name || '';

  return (
    <div style={overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modal}>
        <div style={{ fontSize: '15px', fontWeight: 700, color: C.text, marginBottom: '20px' }}>
          {isEdit ? 'Edit Department' : 'Add Department'}
        </div>

        {err && (
          <div style={{ background: C.red + '22', border: `1px solid ${C.red}44`, borderRadius: '8px', padding: '10px 12px', marginBottom: '14px', fontSize: '12px', color: C.red }}>
            {err}
          </div>
        )}

        {/* Company */}
        <div style={field}>
          <label style={label}>Company *</label>
          {isEdit
            ? <input style={readSt} value={companyName} readOnly />
            : (
              <select style={inputSt} value={form.companyId} onChange={e => setCompany(e.target.value)}>
                <option value=''>— Select company —</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )
          }
        </div>

        {/* Site */}
        <div style={field}>
          <label style={label}>Site *</label>
          {isEdit
            ? <input style={readSt} value={siteName} readOnly />
            : (
              <select style={inputSt} value={form.siteId} onChange={e => setForm(f => ({ ...f, siteId: e.target.value }))}
                disabled={!form.companyId}>
                <option value=''>— Select site —</option>
                {filteredSites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            )
          }
        </div>

        {/* Name */}
        <div style={field}>
          <label style={label}>Department Name *</label>
          <input style={inputSt} value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder='e.g. Engineering' />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '4px' }}>
          <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '9px 18px', borderRadius: '8px', border: 'none', background: C.blue, color: '#fff', fontSize: '13px', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main View ────────────────────────────────────────────────────────────────

export default function DepartmentsView({ departments, companies, sites, onAdd, onUpdate, onRemove }) {
  const [filterCompany, setFilterCompany] = useState('');
  const [search,        setSearch]        = useState('');
  const [showModal,     setShowModal]     = useState(false);
  const [editTarget,    setEditTarget]    = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);  // dept object
  const [deleting,      setDeleting]      = useState(false);
  const [delErr,        setDelErr]        = useState('');

  const filtered = departments.filter(d => {
    if (filterCompany && d.companyId !== filterCompany) return false;
    if (search && !d.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  function openAdd()       { setEditTarget(null); setShowModal(true); }
  function openEdit(dept)  { setEditTarget(dept); setShowModal(true); }
  function closeModal()    { setShowModal(false); setEditTarget(null); }

  function handleSaved(dept) {
    if (editTarget) onUpdate(dept);
    else            onAdd(dept);
    closeModal();
  }

  async function handleDelete(dept) {
    setDeleting(true); setDelErr('');
    try {
      await removeDept(dept.id);
      onRemove(dept.id);
      setConfirmDelete(null);
    } catch (e) {
      setDelErr(e.response?.data?.error || 'Delete failed');
    }
    setDeleting(false);
  }

  const btnBase = { padding: '7px 14px', borderRadius: '7px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '5px', border: 'none' };
  const inputSt = { padding: '7px 12px', borderRadius: '8px', border: `1px solid ${C.border}`, background: C.elevated, color: C.text, fontSize: '13px', fontFamily: 'inherit', outline: 'none' };

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '28px', background: C.bg }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: 700, color: C.text, marginBottom: '4px' }}>Departments</h2>
          <p style={{ fontSize: '13px', color: C.muted }}>
            Manage departments scoped to each company and site.
          </p>
        </div>
        <button onClick={openAdd} style={{ ...btnBase, background: C.blue, color: '#fff', padding: '9px 18px' }}>
          <Plus size={14} /> Add Department
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)} style={{ ...inputSt, minWidth: '180px' }}>
          <option value=''>All Companies</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder='Search departments…'
          style={{ ...inputSt, minWidth: '200px' }} />
      </div>

      {/* Table */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '12px', overflow: 'hidden' }}>
        {/* Table header */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '0', padding: '10px 16px', borderBottom: `1px solid ${C.border}`, background: C.elevated }}>
          {['Department', 'Company', 'Site', ''].map((h, i) => (
            <span key={i} style={{ fontSize: '11px', fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</span>
          ))}
        </div>

        {filtered.length === 0 && (
          <div style={{ padding: '32px 16px', textAlign: 'center', color: C.muted, fontSize: '13px' }}>
            {departments.length === 0 ? 'No departments yet — click Add Department to create the first one.' : 'No departments match your filters.'}
          </div>
        )}

        {filtered.map(dept => {
          const company = companies.find(c => c.id === dept.companyId);
          const site    = sites.find(s => s.id === dept.siteId);
          return (
            <div key={dept.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '0', padding: '12px 16px', borderBottom: `1px solid ${C.border}`, alignItems: 'center' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: C.text }}>{dept.name}</span>
              <span style={{ fontSize: '12px', color: C.subtle }}>{company?.name || '—'}</span>
              <span style={{ fontSize: '12px', color: C.subtle }}>{site?.name || '—'}</span>
              <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                <button onClick={() => openEdit(dept)}
                  style={{ ...btnBase, background: C.elevated, color: C.subtle, padding: '5px 10px' }}
                  title="Edit">
                  <Edit2 size={12} />
                </button>
                <button onClick={() => { setConfirmDelete(dept); setDelErr(''); }}
                  style={{ ...btnBase, background: C.red + '22', color: C.red, border: `1px solid ${C.red}44`, padding: '5px 10px' }}
                  title="Delete">
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Inline delete confirmation */}
      {confirmDelete && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setConfirmDelete(null); }}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '24px', width: '360px', maxWidth: '95vw' }}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: C.text, marginBottom: '8px' }}>Delete Department</div>
            <div style={{ fontSize: '13px', color: C.subtle, marginBottom: '16px' }}>
              Delete <strong style={{ color: C.text }}>{confirmDelete.name}</strong>? Employees assigned to this department will have their department cleared.
            </div>
            {delErr && <div style={{ background: C.red + '22', border: `1px solid ${C.red}44`, borderRadius: '8px', padding: '8px 12px', marginBottom: '12px', fontSize: '12px', color: C.red }}>{delErr}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => setConfirmDelete(null)} style={{ padding: '8px 16px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>
                Cancel
              </button>
              <button onClick={() => handleDelete(confirmDelete)} disabled={deleting}
                style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: C.red, color: '#fff', fontSize: '13px', fontWeight: 600, cursor: deleting ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: deleting ? 0.7 : 1 }}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Form modal */}
      {showModal && (
        <DepartmentFormModal
          data={editTarget}
          companies={companies}
          sites={sites}
          onSave={handleSaved}
          onClose={closeModal}
        />
      )}
    </div>
  );
}
